import { useContext, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import type { ApiRequestProps } from '../../../common/interface';
import { collectionSchema } from '../../../common/schema';
import type { Collection } from '../../../common/type';
import { COLLECTION_QUERY_KEY } from '../constants';

export interface CollectionRequestParams extends ApiRequestProps {
  id?: string;
  pathname?: string;
  designation?: string;
  provider?: string;
}

export const useCollection = (params: CollectionRequestParams) => {
  const { COMMUNITY_FUNCTIONS_URL } = useContext(ConfigContext);
  const { baseURL, id, pathname, enabled = true, ...restParams } = params;
  const [isInitialLoaded, setInitialLoaded] = useState(false);
  const { access_token } = useContext(LoginContext);
  const parsedBaseURL = baseURL || COMMUNITY_FUNCTIONS_URL;

  const axiosConfig = {
    baseURL: `${parsedBaseURL}/store/v1/collections${id ? `/${id}` : ''}`,
    headers: {
      ...(access_token && !id && { authorization: `Bearer ${access_token}` }),
    },
  };
  const api = axios.create(axiosConfig);

  return {
    isInitialLoaded,
    ...useQuery<Collection | null>({
      queryKey: [COLLECTION_QUERY_KEY, parsedBaseURL, id],
      queryFn: async () => {
        const response = await api
          .get('', { params: { provider: 'aws', ...restParams } })
          .catch(reason => {
            // eslint-disable-next-line no-console
            console.error('Error fetching Collections data: ', reason);
            return reason.response;
          });
        if (!isInitialLoaded) {
          setInitialLoaded(true);
        }
        const parsedResponse = collectionSchema.safeParse(response?.data);
        if (parsedResponse.success) {
          return parsedResponse.data;
        }
        // eslint-disable-next-line no-console
        console.error('Error parsing Collection data: ', parsedResponse.error);
        return null;
      },
      enabled,
    }),
  };
};
